import { useMemo } from "react";
import {
  TrendingUp,
  Users,
  CreditCard,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Wallet,
  Clock,
  UserX,
  Calendar,
  Banknote,
  Loader2,
} from "lucide-react";
import useOrangeCameroonBalance from "../../hooks/payments/useOrangeCameroonBalance";
import useOrangeCameroonDisbursementBalance from "../../hooks/payments/useOrangeCameroonDisbursementBalance";
import useMtnCameroonCollectionBalance from "../../hooks/payments/useMtnCameroonCollectionBalance";
import useMtnCameroonDisbursementBalance from "../../hooks/payments/useMtnCameroonDisbursementBalance";
import { Layout } from "../../layouts/DashboardLayout";
import { StatCard } from "../../components/shared/StatCard";
import { useDashboardStats } from "../../hooks/dashboard/useDashboard";

export default function DashboardPage() {
  const { stats, loading } = useDashboardStats();
  const { balance: mtnDisbursementBalance, loading: balanceLoading } = useMtnCameroonDisbursementBalance();
  const { balance: orangeCameroonBalance, loading: orangeBalanceLoading } = useOrangeCameroonBalance();
  const { balance: orangeCameroonDisbursementBalance, loading: orangeDisbursementBalanceLoading } =
    useOrangeCameroonDisbursementBalance();
  const { balance: mtnCollectionBalance, loading: mtnCollectionLoading } = useMtnCameroonCollectionBalance();

  if (loading) {
    return (
      <Layout>
        <div className="p-8 mx-auto max-w-6xl">
          <div className="space-y-6 animate-pulse">
            <div className="w-64 h-8 bg-gray-200 rounded"></div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!stats) return null;

  return (
    <Layout>
      <div className="space-y-6 max-w-7xl">
        {/* En-tête */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vue d'ensemble</h1>
          <p className="mt-1 text-gray-600">Statistiques principales du système</p>
        </div>

        {/* Balances des opérateurs - inchangé */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2">
          {/* MTN Cameroon Disbursement */}
          <div className="p-5 text-white bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg shadow-lg">
            <div className="flex justify-between items-center">
              <div className="flex-1">
                <p className="text-sm font-medium opacity-90">MTN Cameroon Disbursement</p>
                {balanceLoading ? (
                  <div className="flex gap-2 items-center mt-1">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <p className="text-xl font-bold">Chargement...</p>
                  </div>
                ) : mtnDisbursementBalance && mtnDisbursementBalance.mtn_disbursement_balance !== undefined ? (
                  <>
                    <p className="mt-1 text-3xl font-bold">
                      {mtnDisbursementBalance.mtn_disbursement_balance.toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                      })}{" "}
                      {mtnDisbursementBalance.currency}
                    </p>
                    <p className="mt-1 text-sm opacity-75">
                      ≈ $
                      {(mtnDisbursementBalance.mtn_disbursement_balance / 655).toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                      })}{" "}
                      CAD
                    </p>
                  </>
                ) : (
                  <p className="mt-1 text-xl font-bold">-- XAF</p>
                )}
              </div>
              <Wallet className="w-14 h-14 opacity-20" />
            </div>
          </div>

          {/* MTN Cameroon Collection */}
          <div className="p-5 text-white bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-lg shadow-lg">
            <div className="flex justify-between items-center">
              <div className="flex-1">
                <p className="text-sm font-medium opacity-90">MTN Cameroon Collection</p>
                {mtnCollectionLoading ? (
                  <div className="flex gap-2 items-center mt-1">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <p className="text-xl font-bold">Chargement...</p>
                  </div>
                ) : mtnCollectionBalance && mtnCollectionBalance.mtn_collection_balance !== undefined ? (
                  <>
                    <p className="mt-1 text-3xl font-bold">
                      {mtnCollectionBalance.mtn_collection_balance.toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                      })}{" "}
                      {mtnCollectionBalance.currency}
                    </p>
                    <p className="mt-1 text-sm opacity-75">
                      ≈ $
                      {(mtnCollectionBalance.mtn_collection_balance / 655).toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                      })}{" "}
                      CAD
                    </p>
                  </>
                ) : (
                  <p className="mt-1 text-xl font-bold">-- XAF</p>
                )}
              </div>
              <Banknote className="w-14 h-14 opacity-20" />
            </div>
          </div>

          {/* Orange Cameroon Collection */}
          <div className="p-5 text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg">
            <div className="flex justify-between items-center">
              <div className="flex-1">
                <p className="text-sm font-medium opacity-90">Orange Cameroon Collection</p>
                {orangeBalanceLoading ? (
                  <div className="flex gap-2 items-center mt-1">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <p className="text-xl font-bold">Chargement...</p>
                  </div>
                ) : orangeCameroonBalance && orangeCameroonBalance.orange_disbursement_balance !== undefined ? (
                  <>
                    <p className="mt-1 text-3xl font-bold">
                      {orangeCameroonBalance.orange_disbursement_balance.toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                      })}{" "}
                      {orangeCameroonBalance.currency}
                    </p>
                    <p className="mt-1 text-sm opacity-75">
                      {orangeCameroonBalance.firstName} {orangeCameroonBalance.lastName}
                    </p>
                  </>
                ) : (
                  <p className="mt-1 text-xl font-bold">-- XAF</p>
                )}
              </div>
              <Banknote className="w-14 h-14 opacity-20" />
            </div>
          </div>

          {/* Orange Cameroon Disbursement */}
          <div className="p-5 text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg shadow-lg">
            <div className="flex justify-between items-center">
              <div className="flex-1">
                <p className="text-sm font-medium opacity-90">Orange Cameroon Disbursement</p>
                {orangeDisbursementBalanceLoading ? (
                  <div className="flex gap-2 items-center mt-1">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <p className="text-xl font-bold">Chargement...</p>
                  </div>
                ) : orangeCameroonDisbursementBalance &&
                  orangeCameroonDisbursementBalance.orange_disbursement_balance !== undefined ? (
                  <>
                    <p className="mt-1 text-3xl font-bold">
                      {orangeCameroonDisbursementBalance.orange_disbursement_balance.toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                      })}{" "}
                      {orangeCameroonDisbursementBalance.currency}
                    </p>
                    <p className="mt-1 text-sm opacity-75">
                      {orangeCameroonDisbursementBalance.firstName} {orangeCameroonDisbursementBalance.lastName}
                    </p>
                  </>
                ) : (
                  <p className="mt-1 text-xl font-bold">-- XAF</p>
                )}
              </div>
              <Wallet className="w-14 h-14 opacity-20" />
            </div>
          </div>
        </div>

        {/* Statistiques principales */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
          <StatCard
            icon={CreditCard}
            label="Paiements"
            value={stats.totalPayments.toLocaleString()}
            sublabel={`${stats.percentageOfSuccessPayments}% de réussite`}
            color="blue"
          />

          <StatCard
            icon={DollarSign}
            label="Revenus"
            value={`$${stats.totalRevenueCad.toLocaleString()}`}
            sublabel={`+$${stats.totalFees.toLocaleString()} frais`}
            color="green"
          />

          <StatCard
            icon={Users}
            label="Utilisateurs"
            value={stats.totalUsers.toLocaleString()}
            sublabel={`${stats.totalVerifiedUsers} vérifiés`}
            color="purple"
          />

          <StatCard
            icon={CheckCircle}
            label="Paiements réussis"
            value={stats.totalSuccessPayments.toLocaleString()}
            color="green"
          />

          <StatCard
            icon={Calendar}
            label="Paiements aujourd'hui"
            value={stats.paymentsTodayTotal.toLocaleString()}
            sublabel={`$${stats.revenueToday.toFixed(0)} revenus`}
            color="blue"
          />
        </div>

        {/* Statistiques secondaires */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
          <StatCard
            icon={XCircle}
            label="Paiements échoués"
            value={stats.totalErrorPayments.toLocaleString()}
            color="red"
          />

          <StatCard
            icon={TrendingUp}
            label="Taux de réussite"
            value={`${stats.percentageOfSuccessPayments}%`}
            color="green"
          />

          <StatCard
            icon={Users}
            label="Utilisateurs actifs"
            value={stats.totalActiveUsers.toLocaleString()}
            sublabel={`sur ${stats.totalUsers} total`}
            color="blue"
          />

          <StatCard
            icon={AlertTriangle}
            label="Pays disponibles"
            value={stats.totalCountries.toLocaleString()}
            color="orange"
          />

          {mtnDisbursementBalance && (
            <StatCard
              icon={Wallet}
              label="MTN Disbursement"
              value={`${(mtnDisbursementBalance.mtn_disbursement_balance / 1000).toFixed(0)}K`}
              sublabel={`${mtnDisbursementBalance.currency}`}
              color="green"
            />
          )}

          <StatCard
            icon={Clock}
            label="En traitement"
            value={stats.totalPendingPayments.toLocaleString()}
            sublabel="Paiements en cours"
            color="orange"
          />

          <StatCard
            icon={UserX}
            label="Non vérifiés"
            value={stats.totalUnverifiedUsers.toLocaleString()}
            sublabel="Utilisateurs KYC"
            color="red"
          />

          <StatCard
            icon={Banknote}
            label="Total FCFA"
            value={`${(stats.totalRevenueFcfaCmr / 1000000).toFixed(1)}M`}
            sublabel="Montant envoyé"
            color="purple"
          />
        </div>

        {/* Top opérateurs */}
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <h3 className="mb-3 text-lg font-semibold text-gray-900">Top Opérateurs</h3>
          <div className="space-y-2">
            {stats.topOperatorsFormatted.map(([operator, count]: [string, number], index: number) => (
              <div key={operator} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex gap-3 items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      index === 0 ? "bg-yellow-500" : index === 1 ? "bg-gray-400" : "bg-amber-600"
                    }`}
                  >
                    {index + 1}
                  </div>
                  <span className="font-medium text-gray-900">{operator}</span>
                </div>
                <span className="text-lg font-bold text-gray-700">{count} paiements</span>
              </div>
            ))}
          </div>
        </div>

        {/* Résumé rapide */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
          <h3 className="mb-3 text-lg font-semibold text-gray-900">Résumé</h3>
          <div className="grid grid-cols-1 gap-4 text-center md:grid-cols-5">
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {((stats.totalVerifiedUsers / stats.totalUsers) * 100).toFixed(0)}%
              </p>
              <p className="text-sm text-gray-600">Utilisateurs vérifiés</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.percentageOfSuccessPayments}%</p>
              <p className="text-sm text-gray-600">Taux de réussite</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">
                ${(stats.totalRevenueCad / (stats.totalSuccessPayments || 1)).toFixed(0)}
              </p>
              <p className="text-sm text-gray-600">Revenus moyens par transaction</p>
            </div>
            {mtnDisbursementBalance && (
              <div>
                <p className="text-2xl font-bold text-emerald-600">
                  {(mtnDisbursementBalance.mtn_disbursement_balance / 1000).toFixed(1)}K XAF
                </p>
                <p className="text-sm text-gray-600">MTN Disbursement</p>
              </div>
            )}
            <div>
              <p className="text-2xl font-bold text-orange-600">${stats.totalFees.toFixed(0)}</p>
              <p className="text-sm text-gray-600">Total des frais</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
